/* Shared document-attachment widget. Used by:
   - bank transfer modal           (entityType="bank_transfer")
   - transaction add/edit          (entityType="transaction")
   - finance wizard step 6         (entityType="finance_app")

   Two modes:
   - When entityId is set: live mode — every upload/delete hits the server
     immediately and the list re-fetches. Suitable for editing existing rows.
   - When entityId is null: staging mode — files are held in component state
     and the parent reads them via `getStaged()` after creating the entity,
     then uploads them to the new id. Suitable for "new" flows where the
     entity doesn't exist yet.

   `editable` controls whether the upload + delete + rename actions render.
   The list itself is always shown (read-only fallback). */

const { useState: useStateD, useEffect: useEffectD, useRef: useRefD, useImperativeHandle: useImperativeHandleD, forwardRef: forwardRefD, useCallback: useCallbackD } = React;

function fmtBytes(n) {
  if (!n && n !== 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

// Auth-gated download. Native <a download> can't carry the bearer
// header, so fetch the file ourselves and stream it through a blob URL.
async function downloadDoc(doc) {
  const token = window.QH_API.getToken();
  const r = await fetch(`/api/v1/documents/${doc.id}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!r.ok) {
    let msg = `Download failed (${r.status})`;
    try { const j = await r.json(); if (j && j.error) msg = j.error; } catch {}
    throw new Error(msg);
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = doc.fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

const DocumentAttachments = forwardRefD(function DocumentAttachments(
  { entityType, entityId, editable = true, title = 'Attached documents', emptyMsg = 'No documents attached.' },
  ref
) {
  const [docs, setDocs] = useStateD([]);
  const [staged, setStaged] = useStateD([]); // File[] held until parent saves
  const [busy, setBusy] = useStateD(false);
  const [err, setErr] = useStateD('');
  const fileInputRef = useRefD(null);

  const reload = useCallbackD(async () => {
    if (!entityId) return;
    try {
      const list = await window.QH_API.get(`/documents/${entityType}/${entityId}`);
      setDocs(list || []);
    } catch (e) {
      setErr(e.message || 'Failed to load documents.');
    }
  }, [entityType, entityId]);

  useEffectD(() => { reload(); }, [reload]);

  // Staging-mode flush: parent calls this after creating the entity.
  // Returns the new doc count so the parent can update its UI.
  useImperativeHandleD(ref, () => ({
    hasStaged: () => staged.length > 0,
    async flushStaged(newEntityId) {
      if (!staged.length) return [];
      const fd = new FormData();
      for (const f of staged) fd.append('files', f, f.name);
      const r = await window.QH_API.postForm(`/documents/${entityType}/${newEntityId}`, fd);
      setStaged([]);
      return r || [];
    },
  }), [staged, entityType]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (!files.length) return;
    setErr('');
    if (!entityId) {
      // Staging mode — accumulate files locally.
      setStaged(prev => [...prev, ...files]);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append('files', f, f.name);
      const r = await window.QH_API.postForm(`/documents/${entityType}/${entityId}`, fd);
      setDocs(r || []);
    } catch (e) { setErr(e.message || 'Upload failed.'); }
    finally { setBusy(false); }
  };

  const onDelete = async (doc) => {
    if (!confirm(`Delete "${doc.fileName}"?`)) return;
    setBusy(true); setErr('');
    try {
      await window.QH_API.delete(`/documents/${doc.id}`);
      await reload();
    } catch (e) { setErr(e.message || 'Delete failed.'); }
    finally { setBusy(false); }
  };

  const onRename = async (doc) => {
    const next = prompt('Rename file to:', doc.fileName);
    if (!next || next === doc.fileName) return;
    setBusy(true); setErr('');
    try {
      await window.QH_API.put(`/documents/${doc.id}`, { fileName: next });
      await reload();
    } catch (e) { setErr(e.message || 'Rename failed.'); }
    finally { setBusy(false); }
  };

  const removeStaged = (idx) => setStaged(prev => prev.filter((_, i) => i !== idx));

  const totalCount = docs.length + staged.length;

  return (
    <div className="card" style={{borderColor:'var(--border)'}}>
      <div className="card-header">
        {title}
        <span className="actions">
          <span className="muted text-xs">{totalCount} attached{!entityId && staged.length > 0 ? ' (will save with this record)' : ''}</span>
        </span>
      </div>
      <div className="card-body" style={{display:'flex',flexDirection:'column',gap:8}}>
        {totalCount === 0 && <div className="text-xs muted" style={{padding:'4px 0'}}>{emptyMsg}</div>}

        {docs.length > 0 && (
          <div className="table-wrap" style={{border:'none'}}>
            <table className="data">
              <thead>
                <tr>
                  <th>File</th>
                  <th style={{width:90}} className="right">Size</th>
                  <th style={{width:160}}>Uploaded by</th>
                  <th style={{width:160}}>When</th>
                  <th style={{width:130}}></th>
                </tr>
              </thead>
              <tbody>
                {docs.map(d => (
                  <tr key={d.id}>
                    <td>
                      <a href="#" onClick={(e) => { e.preventDefault(); downloadDoc(d).catch(err => alert(err.message)); }} title="Download">
                        <I.FileText s={11}/> {d.fileName}
                      </a>
                    </td>
                    <td className="num right font-mono text-xs">{fmtBytes(d.sizeBytes)}</td>
                    <td className="text-xs">{d.uploadedByName || '—'}</td>
                    <td className="text-xs font-mono">{window.fmtDateTime(d.uploadedAt)}</td>
                    <td>
                      {editable && (
                        <div style={{display:'flex',gap:4,justifyContent:'flex-end'}}>
                          <button className="icon-btn" title="Rename" onClick={() => onRename(d)} disabled={busy}><I.Edit s={11}/></button>
                          <button className="icon-btn" title="Delete" onClick={() => onDelete(d)} disabled={busy}><I.Trash s={11}/></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {staged.length > 0 && (
          <div style={{padding:8,background:'var(--bg-sunken)',borderRadius:6,display:'flex',flexDirection:'column',gap:4}}>
            <div className="text-xs muted uppercase">Staged for upload</div>
            {staged.map((f, i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,fontSize:13}}>
                <I.FileText s={11}/>
                <span style={{flex:1}}>{f.name}</span>
                <span className="text-xs muted font-mono">{fmtBytes(f.size)}</span>
                <button className="icon-btn" title="Remove" onClick={() => removeStaged(i)}><I.X s={11}/></button>
              </div>
            ))}
          </div>
        )}

        {err && <div style={{padding:'8px 12px',background:'var(--bg-sunken)',color:'var(--neg)',borderRadius:6,fontSize:12}}>{err}</div>}

        {editable && (
          <div>
            <input ref={fileInputRef} type="file" multiple style={{display:'none'}} onChange={onPick}/>
            <button className="btn sm" onClick={() => fileInputRef.current?.click()} disabled={busy}>
              <I.Plus s={11}/> {busy ? 'Uploading…' : 'Add document(s)'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

window.DocumentAttachments = DocumentAttachments;
